<template>
  <Container>
    <div class="ding-dash">
      <div class="ding-dash-toolbar">
        <div>
          <div class="ding-dash-title">钉钉数据仪表盘</div>
          <div class="ding-dash-sub">流程在途 / 审批耗时 · 入职离职 · 人员构成</div>
        </div>
        <div class="ding-toolbar-right">
          <Button
            v-if="warningRows.length"
            class="ding-warn-btn"
            icon="md-alert"
            @click="warningModal = true"
          >
            {{ warningRows.length }} 项接口异常
          </Button>
          <span class="ding-muted" v-if="cacheHint">{{ cacheHint }}</span>
          <Select v-model="days" style="width:120px;" @on-change="() => reloadAll(false)">
            <Option :value="30">近 30 天</Option>
            <Option :value="90">近 90 天</Option>
            <Option :value="120">近 120 天</Option>
          </Select>
          <Button icon="md-refresh" :loading="loadingAny" @click="reloadAll(true)">强制刷新</Button>
        </div>
      </div>

      <Modal
        v-model="warningModal"
        title="钉钉接口调用异常明细"
        width="860"
        :mask-closable="true"
      >
        <p class="ding-warn-tip">
          下列接口调用失败后已降级展示。若权限已开通，请对照「错误信息」排查：应用未选中、权限未生效、限流、超时或企业未开通智能人事等。
        </p>
        <BaseTable
          :columns="warningColumns"
          :data="warningRows"
          border
          size="small"
        />
        <div slot="footer">
          <Button @click="warningModal = false">关闭</Button>
          <Button type="primary" :loading="loadingAny" @click="reloadAll(true)">重新加载</Button>
        </div>
      </Modal>

      <div class="ding-stat-grid">
        <div
          class="ding-stat-card is-link"
          v-for="item in summaryCards"
          :key="item.key"
          @click="go(item.route)"
        >
          <div class="ding-stat-label">{{ item.label }}</div>
          <div class="ding-stat-value">{{ item.value }}</div>
          <div class="ding-stat-desc">{{ item.desc }}</div>
        </div>
      </div>

      <div class="ding-section-head">
        <h3>流程分析</h3>
        <Tag v-if="workflow.loading" color="blue">加载中…</Tag>
        <span v-else class="ding-muted">近 {{ days }} 天 · 使用量排行</span>
        <a class="ding-link" @click="go('processes')">全部流程</a>
        <a class="ding-link" @click="go('instances')">实例列表</a>
      </div>

      <div class="ding-stat-grid ding-stat-grid-sm">
        <div
          class="ding-stat-card is-link"
          v-for="item in workflowCards"
          :key="'wf-' + item.key"
          @click="go(item.route)"
        >
          <div class="ding-stat-label">{{ item.label }}</div>
          <div class="ding-stat-value">{{ item.value }}</div>
          <div class="ding-stat-desc">{{ item.desc }}</div>
        </div>
      </div>

      <div class="ding-rank-card">
        <div class="ding-chart-title">流程使用排行榜（在途 + 完成）</div>
        <div v-if="!processRank.length && !workflow.loading" class="ding-muted" style="padding:20px 0;">暂无流程数据</div>
        <div
          class="ding-rank-item"
          v-for="(row, idx) in processRank"
          :key="row.processCode || idx"
          @click="go(`instances?processCode=${encodeURIComponent(row.processCode || '')}`)"
        >
          <div class="ding-rank-no" :class="'rank-' + (idx + 1)">{{ idx + 1 }}</div>
          <div class="ding-rank-main">
            <div class="ding-rank-name">{{ row.name || row.processCode }}</div>
            <div class="ding-rank-meta">
              <span>在途 {{ row.running || 0 }}</span>
              <span>完成 {{ row.completed || 0 }}</span>
              <span v-if="row.avgHours != null">均耗 {{ fmtNum(row.avgHours) }}h</span>
            </div>
            <div class="ding-rank-bar">
              <div class="ding-rank-bar-inner" :style="{width: rankBarWidth(row) + '%'}"></div>
            </div>
          </div>
          <div class="ding-rank-total">{{ (row.running || 0) + (row.completed || 0) }}</div>
        </div>
      </div>

      <div class="ding-section-head" style="margin-top:24px;">
        <h3>人事分析</h3>
        <Tag v-if="hr.loading" color="blue">加载中…</Tag>
        <span v-else class="ding-muted">在岗 {{ hrSummary.headcount }} · 待入职 {{ hrSummary.preentry }}</span>
        <a class="ding-link" @click="go('users')">人员管理</a>
        <a class="ding-link" @click="go('deps')">组织架构</a>
      </div>

      <div class="ding-stat-grid ding-stat-grid-sm">
        <div
          class="ding-stat-card is-link"
          v-for="item in hrCards"
          :key="'hr-' + item.key"
          @click="go(item.route)"
        >
          <div class="ding-stat-label">{{ item.label }}</div>
          <div class="ding-stat-value">{{ item.value }}</div>
          <div class="ding-stat-desc">{{ item.desc }}</div>
        </div>
      </div>

      <div class="ding-chart-row">
        <div class="ding-chart-card">
          <div class="ding-chart-title">入职 / 离职趋势</div>
          <div ref="chartHireLeave" class="ding-chart"></div>
        </div>
        <div class="ding-chart-card">
          <div class="ding-chart-title">在岗状态构成</div>
          <div ref="chartOnjob" class="ding-chart"></div>
        </div>
      </div>

      <div class="ding-chart-row">
        <div class="ding-chart-card">
          <div class="ding-chart-title">部门人数 Top</div>
          <div ref="chartDept" class="ding-chart"></div>
        </div>
        <div class="ding-chart-card">
          <div class="ding-chart-title">职位 / 学历构成</div>
          <div ref="chartCompose" class="ding-chart"></div>
        </div>
      </div>

      <div class="ding-section" v-if="recentLeaves.length">
        <div class="ding-section-head" style="margin-bottom:10px;">
          <h4 style="margin:0;">近 {{ days }} 天离职记录</h4>
          <a class="ding-link" @click="go('users')">去人员管理</a>
        </div>
        <BaseTable :columns="leaveColumns" :data="recentLeaves" border size="small" />
      </div>
    </div>
  </Container>
</template>

<script>
import Container from '@/components/app/container.vue';
import * as echarts from 'echarts';
import moment from 'moment';

export default {
  name: 'DingDashboard',
  components: {Container},
  data() {
    return {
      days: 90,
      overview: {loading: false, data: null},
      workflow: {loading: false, data: null},
      hr: {loading: false, data: null},
      charts: {},
      warningModal: false,
      warningAutoShown: false,
      warningColumns: [{
        title: '模块',
        key: 'title',
        width: 140,
      }, {
        title: '标识',
        key: 'source',
        width: 120,
      }, {
        title: '钉钉接口',
        key: 'api',
        minWidth: 220,
      }, {
        title: '建议权限',
        key: 'permission',
        minWidth: 160,
      }, {
        title: '错误信息',
        key: 'message',
        minWidth: 220,
        render: (h, {row}) => h('span', {
          style: {wordBreak: 'break-all', color: 'var(--error, #ed4014)', fontSize: '12px'},
        }, row.message || '-'),
      }],
      leaveColumns: [{
        title: '姓名',
        key: 'name',
        minWidth: 100,
      }, {
        title: 'userId',
        key: 'userId',
        minWidth: 140,
      }, {
        title: '离职时间',
        key: 'leaveTime',
        width: 170,
        render: (h, {row}) => h('span', this.fmtTime(row.leaveTime)),
      }, {
        title: '原因',
        key: 'leaveReason',
        minWidth: 120,
      }],
    };
  },
  computed: {
    loadingAny() {
      return this.overview.loading || this.workflow.loading || this.hr.loading;
    },
    warningRows() {
      const list = [];
      const seen = {};
      ;[this.overview.data, this.workflow.data, this.hr.data].forEach((d) => {
        if (!d || !Array.isArray(d.warnings)) return;
        d.warnings.forEach((w) => {
          const key = (w.source || '') + '|' + (w.message || '');
          if (seen[key]) return;
          seen[key] = true;
          list.push({
            source: w.source || '-',
            title: w.title || w.source || '-',
            api: w.api || '-',
            permission: w.permission || '-',
            message: w.message || '-',
            code: w.code,
          });
        });
      });
      return list;
    },
    warnings() {
      return this.warningRows;
    },
    summaryCards() {
      const o = this.overview.data || {};
      const wf = (this.workflow.data && this.workflow.data.summary) || {};
      const hr = (this.hr.data && this.hr.data.summary) || {};
      const onjobTotal = (hr.onjob && hr.onjob.total != null) ? hr.onjob.total : o.onjob;
      const deptCount = hr.deptCount != null ? hr.deptCount : o.deps;
      const leftInRange = hr.leftInRange != null ? hr.leftInRange : o.leftInRange;
      return [{
        key: 'onjob',
        label: '在岗人数',
        value: this.fmtInt(onjobTotal),
        desc: '智能人事 / 通讯录',
        route: 'users',
      }, {
        key: 'deps',
        label: '部门',
        value: this.fmtInt(deptCount),
        desc: '组织架构',
        route: 'deps',
      }, {
        key: 'processes',
        label: '审批流程',
        value: this.fmtInt(o.processes),
        desc: '已启用模板',
        route: 'processes',
      }, {
        key: 'running',
        label: '在途实例',
        value: this.fmtInt(wf.running),
        desc: '抽样流程合计',
        route: 'instances',
      }, {
        key: 'hired',
        label: `近${this.days}天入职`,
        value: this.fmtInt(hr.hiredInRange),
        desc: '按入职日期',
        route: 'users',
      }, {
        key: 'left',
        label: `近${this.days}天离职`,
        value: this.fmtInt(leftInRange),
        desc: '离职记录',
        route: 'users',
      }];
    },
    workflowCards() {
      const s = (this.workflow.data && this.workflow.data.summary) || {};
      return [{
        key: 'running',
        label: '在途',
        value: this.fmtInt(s.running),
        desc: '点击查看在途实例',
        route: 'processes',
      }, {
        key: 'completed',
        label: '已完成',
        value: this.fmtInt(s.completed),
        desc: `近 ${this.days} 天 · 流程列表`,
        route: 'processes',
      }, {
        key: 'terminated',
        label: '已撤销',
        value: this.fmtInt(s.terminated),
        desc: '流程列表',
        route: 'processes',
      }, {
        key: 'avg',
        label: '平均审批(h)',
        value: this.fmtNum(s.avgApprovalHours),
        desc: '抽样估算 · 流程列表',
        route: 'processes',
      }];
    },
    hrSummary() {
      return (this.hr.data && this.hr.data.summary) || {};
    },
    hrCards() {
      const s = this.hrSummary;
      const onjob = s.onjob || {};
      return [{
        key: 'headcount',
        label: '花名册人数',
        value: this.fmtInt(s.headcount),
        desc: '人员管理',
        route: 'users',
      }, {
        key: 'formal',
        label: '正式',
        value: this.fmtInt(onjob.formal),
        desc: '人员管理',
        route: 'users',
      }, {
        key: 'trial',
        label: '试用',
        value: this.fmtInt(onjob.trial),
        desc: '人员管理',
        route: 'users',
      }, {
        key: 'leaving',
        label: '待离职',
        value: this.fmtInt(onjob.leaving),
        desc: '人员管理',
        route: 'users',
      }, {
        key: 'preentry',
        label: '待入职',
        value: this.fmtInt(s.preentry),
        desc: '人员管理',
        route: 'users',
      }, {
        key: 'net',
        label: '净增（窗口内）',
        value: this.fmtInt((s.hiredInRange || 0) - (s.leftInRange || 0)),
        desc: '组织架构',
        route: 'deps',
      }];
    },
    processRank() {
      const rows = ((this.workflow.data && this.workflow.data.byProcess) || []).slice();
      rows.sort((a, b) => {
        const ta = (a.running || 0) + (a.completed || 0);
        const tb = (b.running || 0) + (b.completed || 0);
        return tb - ta;
      });
      return rows.slice(0, 10);
    },
    rankMax() {
      if (!this.processRank.length) return 1;
      const top = this.processRank[0];
      return Math.max(1, (top.running || 0) + (top.completed || 0));
    },
    recentLeaves() {
      return (this.hr.data && this.hr.data.recentLeaves) || [];
    },
    cacheHint() {
      const sources = [this.overview.data, this.workflow.data, this.hr.data].filter(Boolean);
      if (!sources.length) return '';
      const anyLive = sources.some((d) => d.cached === false);
      if (anyLive && sources.every((d) => d.cached === false)) return '刚刚实时拉取';
      const times = sources.map((d) => d.cachedAt).filter(Boolean).map(Number);
      if (!times.length) return sources.some((d) => d.cached) ? '已命中缓存' : '';
      const t = Math.min(...times);
      const m = moment(t);
      return m.isValid() ? `缓存 ${m.format('MM-DD HH:mm')}` : '已命中缓存';
    },
  },
  watch: {
    loadingAny(val) {
      if (!val && this.warningRows.length && !this.warningAutoShown) {
        this.warningAutoShown = true;
        this.warningModal = true;
      }
    },
  },
  mounted() {
    // 默认读服务端缓存，避免每次进页打爆钉钉限额
    this.$api.afterLogin(() => this.reloadAll(false));
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    Object.keys(this.charts).forEach((k) => {
      if (this.charts[k]) this.charts[k].dispose();
    });
  },
  methods: {
    unwrap(res) {
      return (res && res.data && res.data.data) || (res && res.data) || {};
    },
    fmtInt(v) {
      if (v == null || v === '') return '—';
      return Number(v).toLocaleString();
    },
    fmtNum(v) {
      if (v == null || !Number.isFinite(Number(v))) return '—';
      return Number(v).toFixed(1);
    },
    fmtTime(t) {
      if (!t) return '-';
      const m = moment(t);
      return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : String(t);
    },
    go(route) {
      if (!route) return;
      this.RouteTo(route);
    },
    rankBarWidth(row) {
      const total = (row.running || 0) + (row.completed || 0);
      return Math.max(4, Math.round((total / this.rankMax) * 100));
    },
    async reloadAll(force = false) {
      this.warningAutoShown = false;
      this.loadOverview(force);
      this.loadWorkflow(force);
      this.loadHr(force);
    },
    forceQs(force) {
      return force ? '&forced=1' : '';
    },
    async loadOverview(force = false) {
      this.overview.loading = true;
      try {
        const res = await this.$api.get(`/ding/dashboard?days=${this.days}${this.forceQs(force)}`);
        this.overview.data = this.unwrap(res);
      } catch (e) {
        this.Error(e);
      } finally {
        this.overview.loading = false;
      }
    },
    async loadWorkflow(force = false) {
      this.workflow.loading = true;
      try {
        // 控制采样规模，配合服务端 2h 缓存，降低超限风险
        const res = await this.$api.get(
          `/ding/dashboard/workflow?days=${this.days}&maxProcesses=15&maxPages=2&sampleDetail=4${this.forceQs(force)}`,
        );
        this.workflow.data = this.unwrap(res);
      } catch (e) {
        this.Error(e);
      } finally {
        this.workflow.loading = false;
      }
    },
    async loadHr(force = false) {
      this.hr.loading = true;
      try {
        const res = await this.$api.get(`/ding/dashboard/hr?days=${this.days}${this.forceQs(force)}`);
        this.hr.data = this.unwrap(res);
        this.$nextTick(() => {
          this.renderHireLeaveChart();
          this.renderOnjobChart();
          this.renderDeptChart();
          this.renderComposeChart();
        });
      } catch (e) {
        this.Error(e);
      } finally {
        this.hr.loading = false;
      }
    },
    ensureChart(key, el) {
      if (!el) return null;
      if (this.charts[key]) {
        this.charts[key].dispose();
      }
      this.charts[key] = echarts.init(el);
      return this.charts[key];
    },
    handleResize() {
      Object.values(this.charts).forEach((c) => c && c.resize());
    },
    chartTheme() {
      return {
        textStyle: {color: '#666'},
        grid: {left: 40, right: 16, top: 36, bottom: 40},
      };
    },
    renderHireLeaveChart() {
      const chart = this.ensureChart('hireLeave', this.$refs.chartHireLeave);
      if (!chart) return;
      const rows = (this.hr.data && this.hr.data.hireLeaveTrend) || [];
      chart.setOption({
        ...this.chartTheme(),
        tooltip: {trigger: 'axis'},
        legend: {data: ['入职', '离职']},
        xAxis: {type: 'category', data: rows.map((r) => r.month)},
        yAxis: {type: 'value', minInterval: 1},
        series: [{
          name: '入职',
          type: 'line',
          smooth: true,
          data: rows.map((r) => r.hired),
          itemStyle: {color: '#2d8cf0'},
        }, {
          name: '离职',
          type: 'line',
          smooth: true,
          data: rows.map((r) => r.left),
          itemStyle: {color: '#ed4014'},
        }],
      });
    },
    renderOnjobChart() {
      const chart = this.ensureChart('onjob', this.$refs.chartOnjob);
      if (!chart) return;
      const onjob = (this.hr.data && this.hr.data.summary && this.hr.data.summary.onjob) || {};
      const status = (this.hr.data && this.hr.data.composition && this.hr.data.composition.byStatus) || [];
      const data = status.length
        ? status
        : [
          {name: '正式', value: onjob.formal || 0},
          {name: '试用', value: onjob.trial || 0},
          {name: '待离职', value: onjob.leaving || 0},
        ];
      chart.setOption({
        ...this.chartTheme(),
        tooltip: {trigger: 'item'},
        series: [{
          type: 'pie',
          radius: ['35%', '65%'],
          data,
          label: {formatter: '{b}: {c}'},
        }],
      });
    },
    renderDeptChart() {
      const chart = this.ensureChart('dept', this.$refs.chartDept);
      if (!chart) return;
      const rows = ((this.hr.data && this.hr.data.composition && this.hr.data.composition.byDept) || []).slice(0, 10).reverse();
      chart.setOption({
        ...this.chartTheme(),
        grid: {left: 100, right: 24, top: 20, bottom: 24},
        tooltip: {trigger: 'axis'},
        xAxis: {type: 'value', minInterval: 1},
        yAxis: {
          type: 'category',
          data: rows.map((r) => r.name),
          axisLabel: {fontSize: 11},
        },
        series: [{
          type: 'bar',
          data: rows.map((r) => r.value),
          itemStyle: {color: '#2d8cf0'},
        }],
      });
    },
    renderComposeChart() {
      const chart = this.ensureChart('compose', this.$refs.chartCompose);
      if (!chart) return;
      const titles = ((this.hr.data && this.hr.data.composition && this.hr.data.composition.byTitle) || []).slice(0, 8);
      const edus = ((this.hr.data && this.hr.data.composition && this.hr.data.composition.byEducation) || []).slice(0, 8);
      chart.setOption({
        ...this.chartTheme(),
        tooltip: {trigger: 'axis'},
        legend: {data: ['职位', '学历']},
        xAxis: {
          type: 'category',
          data: Array.from({length: Math.max(titles.length, edus.length)}, (_, i) => i + 1),
          axisLabel: {show: false},
        },
        yAxis: {type: 'value', minInterval: 1},
        series: [{
          name: '职位',
          type: 'bar',
          data: titles.map((r) => ({value: r.value, name: r.name})),
          itemStyle: {color: '#2d8cf0'},
          label: {
            show: true,
            position: 'top',
            fontSize: 10,
            formatter: (p) => (p.data && p.data.name) || '',
          },
        }, {
          name: '学历',
          type: 'bar',
          data: edus.map((r) => ({value: r.value, name: r.name})),
          itemStyle: {color: '#ff9900'},
          label: {
            show: true,
            position: 'top',
            fontSize: 10,
            formatter: (p) => (p.data && p.data.name) || '',
          },
        }],
      });
    },
  },
};
</script>

<style lang="less" scoped>
.ding-dash {
  box-sizing: border-box;
  padding: 16px;
  height: 100%;
  overflow: auto;
  min-height: 0;
}
.ding-dash-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
}
.ding-dash-title {
  font-size: 18px;
  font-weight: 600;
}
.ding-dash-sub {
  margin-top: 4px;
  color: var(--text2);
  font-size: 12px;
}
.ding-warn-btn {
  color: #fff !important;
  background: #e6a23c !important;
  border-color: #e6a23c !important;
  /deep/ .ivu-icon {
    color: #fff !important;
  }
  &:hover {
    color: #fff !important;
    background: #cf9236 !important;
    border-color: #cf9236 !important;
  }
}
.ding-toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ding-warn-tip {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--text2);
  line-height: 1.5;
}
.ding-stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
.ding-stat-grid-sm {
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
}
.ding-stat-card {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 14px;
  cursor: default;
  &.is-link {
    cursor: pointer;
    transition: border-color .15s, transform .15s;
    &:hover {
      border-color: var(--primary);
      transform: translateY(-1px);
    }
  }
}
.ding-link {
  margin-left: 8px;
  font-size: 12px;
  color: var(--primary);
  cursor: pointer;
}
.ding-rank-card {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.ding-rank-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: var(--bg2);
  }
}
.ding-rank-no {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--border);
  color: var(--text2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  &.rank-1 { background: #ff9900; color: #fff; }
  &.rank-2 { background: #2d8cf0; color: #fff; }
  &.rank-3 { background: #19be6b; color: #fff; }
}
.ding-rank-main {
  flex: 1;
  min-width: 0;
}
.ding-rank-name {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ding-rank-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: var(--text2);
  margin-bottom: 6px;
}
.ding-rank-bar {
  height: 6px;
  background: var(--bg2);
  border-radius: 3px;
  overflow: hidden;
}
.ding-rank-bar-inner {
  height: 100%;
  background: linear-gradient(90deg, #2d8cf0, #19be6b);
  border-radius: 3px;
}
.ding-rank-total {
  font-size: 18px;
  font-weight: 600;
  min-width: 40px;
  text-align: right;
}
.ding-stat-label {
  color: var(--text2);
  font-size: 12px;
}
.ding-stat-value {
  font-size: 24px;
  font-weight: 600;
  margin: 6px 0 2px;
}
.ding-stat-desc {
  color: var(--subtext2);
  font-size: 11px;
}
.ding-section-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0 12px;
  h3 {
    margin: 0;
    font-size: 15px;
  }
}
.ding-muted {
  color: var(--text2);
  font-size: 12px;
}
.ding-chart-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}
.ding-chart-card {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 12px;
}
.ding-chart-title {
  font-size: 13px;
  margin-bottom: 4px;
  color: var(--text2);
}
.ding-chart {
  height: 280px;
  width: 100%;
}
.ding-section {
  margin-bottom: 16px;
}
@media (max-width: 960px) {
  .ding-chart-row {
    grid-template-columns: 1fr;
  }
}
</style>
